import { SdkContext } from '../internal'
import type { FeedResponse } from '@/types/feed'

/** F-165: the Feed's order. */
export type FeedSort = 'recent' | 'activity'

export interface FeedParams {
  page?: number
  limit?: number
  /** The API's `FeedItemType`, singular. */
  type?: 'all' | 'status' | 'content' | 'event' | 'course' | 'call4papers' | 'recognition'
  visibility?: 'public' | 'members' | 'all'
  spaceId?: string
  /** F-170: only status and content posts with this tag (the API ignores case, accents and `#`). */
  tag?: string
  /** F-165: `recent` (default) by publication; `activity` by the newest comment or reply (pinned first either way). */
  sort?: FeedSort
  _t?: number
}

/** F-181: a space as the Feed's side column refers to it (null when the item has no space). */
export interface FeedSidebarSpace {
  id: string
  slug: string
  title: string
  sectionSlug: string | null
}

/** F-181: a person in the side column, name and avatar only. */
export interface FeedSidebarPerson {
  id: string
  name: string | null
  avatar: string | null
}

export interface FeedSidebarEvent {
  id: string
  slug: string
  title: string
  type: 'online' | 'in_person' | 'hybrid'
  startTime: string
  endTime: string | null
  /** The end the API used: `endTime`, or the end of the start day in the event's time zone. */
  endsAt: string
  timezone: string
  isLive: boolean
  imageUrl: string | null
  space: FeedSidebarSpace | null
}

export interface FeedSidebarTrendingItem {
  type: 'content' | 'status'
  id: string
  slug: string | null
  /** The post's title, or the start of the status text. */
  title: string
  imageUrl: string | null
  publishedAt: string
  author: FeedSidebarPerson
  space: FeedSidebarSpace | null
}

export interface FeedSidebarLeaderboardEntry {
  rank: number
  member: FeedSidebarPerson
  points: number
}

/** Only the widgets that are on and have items come back; a missing key means "do not show". */
export interface FeedSidebar {
  events?: { items: FeedSidebarEvent[] }
  trending?: { items: FeedSidebarTrendingItem[] }
  newMembers?: { items: FeedSidebarPerson[] }
  leaderboard?: { items: FeedSidebarLeaderboardEntry[]; space: FeedSidebarSpace | null }
}

export class FeedResource {
  constructor(private ctx: SdkContext) {}

  async list(params: FeedParams = {}) {
    return this.ctx.call<FeedResponse['data']>({ httpMethod: 'GET', path: '/feed' }, { query: params })
  }

  /** The shape is read (and typed) by `useFeed`. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the stats are typed by their one reader, useFeed
  async getStats(): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/feed/stats' })
  }

  /** F-181: the side column of a Feed space (visitors too). Replaces the removed `/feed/recommendations`. */
  async getSidebar(spaceId?: string): Promise<FeedSidebar> {
    return this.ctx.call({ httpMethod: 'GET', path: '/feed/sidebar' }, { query: spaceId ? { spaceId } : {} })
  }

  async react(itemId: string, itemType: string, reactionType: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/feed/{type}/{id}/react`, hasBody: true },
      { type: itemType, id: itemId, body: { type: reactionType } }
    )
  }

  async unreact(itemId: string, itemType: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/feed/{type}/{id}/react` }, { type: itemType, id: itemId })
  }

  async comment(itemId: string, itemType: string, content: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/feed/{type}/{id}/comment`, hasBody: true },
      { type: itemType, id: itemId, body: { content } }
    )
  }

  async pin(itemId: string, itemType: string, pinned: boolean): Promise<unknown> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/feed/{type}/{id}/pin`, hasBody: true },
      { type: itemType, id: itemId, body: { pinned } }
    )
  }
}
